import semver from 'semver';
import { find, reduce, snakeCase, camelCase, isPlainObject, get, isFunction, compact, filter, isArray, map, pick } from 'lodash';
import { proto } from '../../proto/ListenerProto';
import { commonLogger } from '../logger/loggerFactory';
import { META_INFO_PROPERTIES } from '../../constants/backend';

type ErrorParam = Record<string, unknown>;
type ErrorValue<TParams, TResult> = TResult | ((params: TParams) => TResult);
type StringKeyed = Record<string, unknown>;

interface ErrorConfig<TParams extends ErrorParam> {
    name: string;
    message: ErrorValue<TParams, string>;
    fingerprint?: ErrorValue<TParams, unknown[]>;
    extra?: ErrorValue<TParams, unknown>;
    status?: ErrorValue<TParams, unknown>;
}

type CustomErrorInstance = Error & {
    status?: unknown;
    fingerprint?: unknown[];
    extra?: unknown;
};

export const guid = () => {
    function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    }
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

export function objectToCase(fn: (key: string) => string) {
    return (target: unknown): unknown => {
        if (isArray(target)) {
            return map(target, (item: unknown) => objectToCase(fn)(item));
        }
        if (isPlainObject(target)) {
            return reduce(
                target as StringKeyed,
                (acc: StringKeyed, value: unknown, key: string) => {
                    acc[fn(key)] = objectToCase(fn)(value);
                    return acc;
                },
                {} as StringKeyed
            );
        }

        return target;
    };
}

export function objectToSnakeCase(object: unknown) {
    return objectToCase(snakeCase)(object);
}

export function objectToCamelCase(object: unknown) {
    return objectToCase(camelCase)(object);
}

export function uint8toInt16Array(bytes: Uint8Array) {
    return new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
}

/**
 * @deprecated Old Listener APP
 * @param devices
 * @returns {{mics: {isDefaultConsole: boolean, dataFlow: number}[], speakers: string[]}}
 */
export function filterDefaultDevices(devices: Record<string, unknown>[]) {
    const mics = compact([
        find(devices, {
            dataFlow: proto.DataFlow.CAPTURE,
            isDefaultConsole: true
        })
    ]);

    const speakers = compact(
        filter(
            devices,
            (device: Record<string, unknown>) =>
                device.dataFlow === proto.DataFlow.RENDER &&
                !(
                    !device.isDefaultConsole &&
                    device.isDefaultCommunications &&
                    !device.isDefaultMultimedia &&
                    typeof device.description === 'string' &&
                    device.description.match(/headset/i) &&
                    device.description.match(/hands-?free/i)
                )
        )
    );

    return { mics, speakers };
}

export function uint8ToSting(bytes: Uint8Array) {
    return new TextDecoder('utf-8').decode(bytes);
}

export function getDeviceSampleRate(state: unknown) {
    return get(state, 'main.recordingInfo.sampleRateHz');
}

export function callIf<TArgs, TResult>(value: TResult | ((args: TArgs) => TResult), args: TArgs, defaultResult?: TResult) {
    if (isFunction(value)) {
        return (value as (params: TArgs) => TResult)(args) || defaultResult;
    }

    return value || defaultResult;
}

export function invokeIf<TArgs, TResult>(target: unknown, path: string, args: TArgs, defaultResult?: TResult) {
    const value = get(target, path);

    return callIf<TArgs, TResult>(value, args, defaultResult);
}

export function semverDiff(v1: string, v2: string) {
    const a = semver.parse(v1);
    const b = semver.parse(v2);
    if (!a || !b) {
        return null;
    }

    for (const key of Object.getOwnPropertyNames(a)) {
        if (key === 'major' || key === 'minor' || key === 'patch') {
            if (a[key] !== b[key]) {
                return key;
            }
        }
    }

    return null;
}

export const now = () => Date.now();

export const safeStringify = (obj: unknown, space = 2, replacer?: (this: unknown, key: string, value: unknown) => unknown) => {
    try {
        return JSON.stringify(obj, replacer || undefined, space);
    } catch {
        const keys = typeof obj === 'object' && obj ? Object.keys(obj as object) : [];
        const errorMessage = `Failed to stringify obj with keys ${keys}`;
        commonLogger.error(errorMessage);
        return errorMessage;
    }
};

export function createError<TParams extends ErrorParam = ErrorParam>({ name, message, fingerprint, extra, status }: ErrorConfig<TParams>) {
    class CustomError extends Error implements CustomErrorInstance {
        status?: unknown;
        fingerprint?: unknown[];
        extra?: unknown;

        constructor(params: TParams = {} as TParams) {
            const msg = typeof message === 'function' ? message(params) || 'no error description' : message;
            super(msg);

            this.name = name;
            this.status = typeof status === 'function' ? status(params) : status;
            this.fingerprint = typeof fingerprint === 'function' ? fingerprint(params) : fingerprint;
            this.extra = typeof extra === 'function' ? extra(params) : extra;

            if (Error.captureStackTrace) {
                Error.captureStackTrace(this, this.constructor);
            }
        }
    }

    Object.defineProperty(CustomError, 'name', { value: name });

    return CustomError as new (params?: TParams) => CustomErrorInstance;
}

export const withPrefix =
    <TParams extends ErrorParam>(prefix: string | ((params: TParams) => string)) =>
    (fn: string | ((params: TParams) => string)) =>
    (params: TParams = {} as TParams) =>
        `${callIf(prefix, params)}${callIf(fn, params)}`;

export function parseId(id: string | number) {
    const parsed = Number.parseInt(String(id), 10);
    return Number.isSafeInteger(parsed) ? parsed : 0;
}

export function filterKeys(items: StringKeyed, cb: (value: unknown, key: string) => boolean) {
    return reduce(
        items,
        (result: StringKeyed, value: unknown, key: string) => {
            if (cb(value, key)) {
                return { ...result, [key]: value };
            }
            return result;
        },
        {} as StringKeyed
    );
}

/**
 * Checks if the passed number is smaller than Int32 max number:2147483647
 *
 * If you pass NaN, string or Infinity it will return true
 * @param {Number} x
 * @returns boolean
 */
export function checkForBigerThanInt32(x: number) {
    if (Number.isNaN(x) || !Number.isFinite(x)) {
        return true;
    }

    return Math.abs(x) >= 2147483647;
}

export function renameKeys(obj: StringKeyed, newKeys: Record<string, string>) {
    const keyValues = Object.keys(obj).map(key => {
        const newKey = newKeys[key] || key;
        return { [newKey]: obj[key] };
    });

    return Object.assign({}, ...keyValues);
}

export function isValidEndpoint(url: string) {
    const expression = /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?/gi;
    const regex = new RegExp(expression);
    return regex.test(url);
}

export function getMetaInfo(startEvent: Record<string, unknown>) {
    const metaInfo = pick(startEvent, META_INFO_PROPERTIES) as Record<string, unknown>;

    for (const key in metaInfo) {
        if (typeof metaInfo[key] !== 'string') {
            metaInfo[key] = JSON.stringify(metaInfo[key]);
        }
    }

    return metaInfo;
}
